import { useCallback, useEffect, useRef, useState } from 'react';
import { TickRing } from '../timer/TickRing';
import { useTicker } from '../timer/useTicker';
import { chime, fmtCountdown } from '../timer/clockUtils';
import { RestAlertToggles, useRestAlertPrefs } from './RestAlertToggles';
import { useFloatingTimer } from '../../lib/useFloatingTimer';

export const RestCountdown = ({ seconds, nextSet, onDone }) => {
  const endRef = useRef(Date.now() + seconds * 1000);
  const [total, setTotal] = useState(seconds * 1000);
  const [left, setLeft] = useState(seconds * 1000);
  const [finished, setFinished] = useState(false);
  const [paused, setPaused] = useState(false);
  const [prefs, togglePref] = useRestAlertPrefs();
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const ctxRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    try { if (Ctx) ctxRef.current = new Ctx(); } catch { /* no audio */ }
    return () => { const c = ctxRef.current; setTimeout(() => c?.close?.().catch(() => {}), 1500); };
  }, []);

  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (prefsRef.current.sound) chime(ctxRef.current);
    if (prefsRef.current.buzz) { try { navigator.vibrate?.([250, 100, 250]); } catch { /* noop */ } }
    setLeft(0); setFinished(true); onDone();
  }, [onDone]);

  // Timeout keeps the alert on time while the app is in the background (animation frames stop there).
  useEffect(() => {
    if (finished || paused) return undefined;
    const id = setTimeout(finish, Math.max(0, endRef.current - Date.now()));
    return () => clearTimeout(id);
  }, [finished, paused, total, finish]);
  const pause = () => setPaused(true);
  const resume = () => { endRef.current = Date.now() + left; setPaused(false); };
  useFloatingTimer(!finished, {
    mode: 'countdown', label: `Rest · next set ${nextSet}`, remainingMs: left, totalMs: total, paused, alert: false,
    onPause: () => pause(), onResume: () => resume(), onSkip: () => onDone(), onAddTime: () => addTime(),
  });

  useTicker(!finished && !paused, useCallback((now) => {
    const ms = Math.max(0, endRef.current - now);
    setLeft(ms);
    if (ms === 0) finish();
  }, [finish]));

  const addTime = () => {
    endRef.current += 15000;
    setLeft((l) => l + 15000);
    setTotal((t) => t + 15000);
  };

  return (
    <div className="flex flex-col items-center" data-testid="setlogger-rest">
      <div className="w-full max-w-[200px]">
        <TickRing progress={total ? left / total : 0}>
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">Rest</span>
          <span className="tabular-nums font-bold text-4xl text-primary leading-none my-1" data-testid="setlogger-rest-display">{fmtCountdown(left)}</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/50">Next: set {nextSet}</span>
        </TickRing>
      </div>
      <div className="mt-3 flex gap-2 w-full">
        <button type="button" onClick={addTime} className="flex-1 h-11 rounded-2xl bg-primary/15 border border-primary/40 text-primary text-sm font-semibold active:scale-[0.98] transition-transform" data-testid="setlogger-rest-add">
          +15s
        </button>
        <button type="button" onClick={onDone} className="flex-1 h-11 rounded-2xl bg-white/[0.06] border border-white/10 text-white text-sm font-semibold active:scale-[0.98] transition-transform" data-testid="setlogger-rest-skip">
          Skip rest
        </button>
      </div>
      <RestAlertToggles prefs={prefs} toggle={togglePref} />
    </div>
  );
};

export default RestCountdown;
